import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Link
} from '@mui/material';
import { ArrowBack, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { userRequest } from '../../requestMethods';
import { LongPostInteractiveTextViewer, TitleInteractiveTextViewer } from '../InteractiveText';
import { TappedWordsWidget } from '../Learning';
import PropTypes from 'prop-types';

const LongPostView = ({ slug: propSlug }) => {
    const navigate = useNavigate();
    
    const [post, setPost] = useState(null);
    const [loadedChunks, setLoadedChunks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tappedWords, setTappedWords] = useState([]);
    const [isWidgetVisible, setIsWidgetVisible] = useState(false);

    const initializePost = useCallback(async () => {
        if (!propSlug) return;
        
        setLoading(true);
        setError(null);

        try {
            const postResponse = await userRequest.get(`/contentLongPost/slug/${propSlug}`);
            setPost(postResponse.data);

            // Load all chunks for display
            // TODO: Implement paging system - load only some chunks at a time for better performance
            // This will be especially important for very long posts to reduce memory usage
            const chunksResponse = await userRequest.get(`/contentLongPost/slug/${propSlug}/formatted-chunks`, {
                params: { start: 0, limit: 100 } // Load all chunks for now, will be paginated later
            });

            setLoadedChunks(chunksResponse.data.chunks);
        } catch (error) {
            console.error('Error fetching post:', error);
            setError('Failed to load the post. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [propSlug]);

    useEffect(() => {
        if (propSlug) {
            initializePost();
        }
    }, [propSlug, initializePost]);

    // Handle word displayed from interactive text
    const handleWordDisplayed = (word) => {
        // Only add words that have translation or grammar link
        if ((word.t && word.t.trim() !== '') || // word.translation
            (word.l && word.l.trim() !== '')) { // word.link
            
            setTappedWords(prevWords => {
                // Check if word already exists (avoid duplicates)
                const wordKey = word.w;
                const exists = prevWords.some(w => 
                    w.w === wordKey
                );
                
                if (!exists) {
                    // Add new word to the beginning of the array
                    return [word, ...prevWords];
                }
                return prevWords;
            });
        }
    };

    const handleToggleWidget = () => {
        setIsWidgetVisible(!isWidgetVisible);
    };

    // Helper function to create anchor ID from text
    const createAnchorId = (text) => {
        return text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/--+/g, '-')
            .trim('-');
    };

    // Get the first chunk as the title chunk for interactive display
    const getTitleChunk = () => {
        if (loadedChunks.length > 0) {
            return loadedChunks.find(chunk => chunk.order === 1);
        }
        return null;
    };

    // Get the first chunk as the title (plain text for TOC and metadata)
    const getPostTitle = () => {
        if (loadedChunks.length > 0) {
            const firstChunk = loadedChunks.find(chunk => chunk.order === 1);
            return firstChunk ? firstChunk.formattedText.replace(/<[^>]*>/g, '') : 'Untitled Post';
        }
        return 'Untitled Post';
    };

    // Create table of contents with anchor links (including main title)
    const createTableOfContents = () => {
        const titleChunks = loadedChunks.filter(chunk => chunk.isTitle);
        
        return titleChunks.map(chunk => {
            const cleanText = chunk.formattedText.replace(/<[^>]*>/g, '');
            return {
                id: createAnchorId(cleanText),
                title: cleanText,
                order: chunk.order,
                isMainTitle: chunk.order === 1
            };
        }).sort((a, b) => a.order - b.order);
    };

    // Get content chunks (skip first chunk for separate display, include everything else)
    const getContentChunks = () => {
        return loadedChunks.filter(chunk => chunk.order !== 1);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 4 }}>
                {error}
            </Alert>
        );
    }

    const postTitle = getPostTitle();
    const titleChunk = getTitleChunk();
    const tocItems = createTableOfContents();
    const contentChunks = getContentChunks();

    return (
        <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                </IconButton>
            </Box>

            {/* Main Layout Container */}
            <Box sx={{ 
                display: 'flex', 
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' }
            }}>
                {/* Main Content Column */}
                <Box sx={{ 
                    flex: 1,
                    minWidth: 0 // Prevent flex item from overflowing
                }}>

            {/* Main Post Title as Interactive Text */}
            {titleChunk ? (
                <Box sx={{ mb: 4, px: { xs: 1, sm: 2 } }}>
                    <TitleInteractiveTextViewer
                        formattedText={titleChunk.formattedText}
                        textId={titleChunk.textId}
                        chunkOrder={titleChunk.order}
                        level={1}
                        anchorId="main-title"
                        centerAlign={true}
                        onWordDisplayed={handleWordDisplayed}
                    />
                    {/* Add subtle decorative line */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 2
                    }}>
                        <Box sx={{
                            width: '60px',
                            height: '4px',
                            backgroundColor: 'primary.main',
                            borderRadius: '2px'
                        }} />
                    </Box>
                </Box>
            ) : (
                <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    id="main-title"
                    sx={{ 
                        fontWeight: 'bold',
                        color: 'primary.main',
                        mb: 3,
                        scrollMarginTop: '20px',
                        textAlign: 'center'
                    }}
                >
                    {postTitle}
                </Typography>
            )}
            
            {post && (
                <Box sx={{ mb: 4 }}>
                    {/* Thumbnail Image */}
                    {post.thumbUrl && (
                        <Box 
                            sx={{ 
                                width: { xs: '100%', md: '50%' },
                                mx: 'auto', // Center horizontally
                                position: 'relative',
                                mb: 2,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                '&::before': {  // Maintain aspect ratio
                                    content: '""',
                                    display: 'block',
                                    paddingTop: '56.25%', // 16:9 aspect ratio
                                }
                            }}
                        >
                            <Box
                                component="img"
                                src={post.thumbUrl}
                                alt={post.title}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain', // Show full image
                                    backgroundColor: 'grey.100', // Light background for transparent images
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.02)'
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {/* Source Info and Metadata */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 2,
                            px: 1
                        }}
                    >
                        {/* Left side - Source info */}
                        <Box>
                            {post.sourceTitle && (
                                <Typography 
                                    variant="subtitle1" 
                                    color="text.secondary"
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center',
                                        gap: 1,
                                        fontStyle: 'italic'
                                    }}
                                >
                                    Source: {post.sourceUrl ? (
                                        <Link
                                            href={post.sourceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ 
                                                color: 'inherit',
                                                textDecoration: 'none',
                                                borderBottom: '1px dotted',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                '&:hover': {
                                                    borderBottom: '1px solid'
                                                }
                                            }}
                                        >
                                            {post.sourceTitle}
                                            <OpenInNew sx={{ fontSize: 16 }} />
                                        </Link>
                                    ) : (
                                        post.sourceTitle
                                    )}
                                </Typography>
                            )}
                            {post.author && (
                                <Typography 
                                    variant="subtitle2" 
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    By {post.author}
                                </Typography>
                            )}
                        </Box>

                        {/* Right side - Reading time and category */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                gap: 1,
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end'
                            }}
                        >
                            <Chip 
                                label={post.category} 
                                color="primary"
                                size="small"
                                sx={{ 
                                    textTransform: 'capitalize',
                                    fontWeight: 500
                                }}
                            />
                            <Chip 
                                label={`${post.estimatedReadingTime || 0} min read`} 
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 500 }}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Table of Contents - Always displayed at the beginning */}
            {tocItems.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Table of Contents
                    </Typography>
                    <List>
                        {tocItems.map((item) => (
                            <ListItem key={item.id} disablePadding>
                                <ListItemButton 
                                    component="a" 
                                    href={item.isMainTitle ? '#main-title' : `#${item.id}`}
                                    sx={{ 
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    <ListItemText 
                                        primary={item.title}
                                        sx={{ 
                                            fontWeight: item.isMainTitle ? 'bold' : 'bold',
                                            color: item.isMainTitle ? 'secondary.main' : 'primary.main'
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {/* Content - Individual chunks with proper spacing */}
            {contentChunks.length > 0 && (
                <Paper sx={{ p: 4, mb: 3 }}>
                    <Box sx={{
                        '& > *': {
                            marginBottom: '0.5rem'
                        },
                        '& > *:last-child': {
                            marginBottom: 0
                        }
                    }}>
                        {contentChunks.map((chunk) => (
                            <Box key={chunk.chunkId}>
                                {chunk.isTitle ? (
                                    <Box sx={{ mb: 2, mt: 3 }}>
                                        <TitleInteractiveTextViewer
                                            formattedText={chunk.formattedText}
                                            textId={chunk.textId}
                                            chunkOrder={chunk.order}
                                            level={5}
                                            anchorId={createAnchorId(chunk.formattedText.replace(/<[^>]*>/g, ''))}
                                            centerAlign={false}
                                            onWordDisplayed={handleWordDisplayed}
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 1 }}>
                                        <LongPostInteractiveTextViewer
                                            formattedText={chunk.formattedText}
                                            textId={chunk.textId}
                                            chunkOrder={chunk.order}
                                            onWordDisplayed={handleWordDisplayed}
                                        />
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Paper>
            )}
                </Box>

                {/* Sidebar Column - Desktop Only */}
                <Box sx={{ 
                    width: { lg: '300px' },
                    display: { xs: 'none', lg: 'block' },
                    flexShrink: 0
                }}>
                    <TappedWordsWidget 
                        tappedWords={tappedWords}
                        variant="sidebar"
                    />
                </Box>
            </Box>

            {/* Mobile Tapped Words Widget */}
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                <TappedWordsWidget 
                    tappedWords={tappedWords}
                    isVisible={isWidgetVisible}
                    onToggleVisibility={handleToggleWidget}
                    variant="floating"
                />
            </Box>
        </Box>
    );
};

LongPostView.propTypes = {
    slug: PropTypes.string
};

export default LongPostView;
